import { useEffect, useRef, useState } from "react";
// `<audio>`'s own `timeupdate` event is deliberately coarse -- the HTML spec only requires it fire
// "every 15 to 250ms", and most real browsers sit at the slow end of that (~250ms, roughly 4Hz) --
// nowhere near precise enough for word-by-word highlighting to look like it's actually tracking the
// voice. At normal speaking pace (multiple words per second), a 250ms gap between updates is long
// enough for the highlight to visibly skip or lag behind, which is what "highlighting not keeping
// aligned with the voice" actually was: the real `wordTimestamps` data and the audio itself were
// never out of sync, `timeupdate`'s own event granularity was just too coarse to render that sync
// smoothly. Polling the element's real `currentTime` every animation frame (~60Hz, same as the
// screen's own repaint rate) while it's actually playing gives highlighting real display-refresh
// precision instead -- paused/seeking still updates once immediately via DOM events, no polling
// needed when nothing is moving.
// `ready` must flip from false to true once the real <audio> element actually exists in the DOM
// (e.g. `Boolean(story?.audioUrl)`) -- callers fetch story/skit data asynchronously and only
// render the <audio> tag once that arrives, but `audioRef` itself is a stable ref object whose
// identity never changes. A real bug found 2026-10-15 debugging "highlighting doesn't work at
// all" (neither word-by-word nor skit per-line): with no `ready` dependency, this effect ran
// exactly once on mount, while `audioRef.current` was still null (the audio element hadn't been
// rendered yet), and never re-ran to attach listeners once the element actually appeared --
// `useEffect(..., [audioRef])` is not useEffect(..., [audioRef.current]); a ref's identity staying
// constant is exactly why it doesn't trigger effect re-runs on its own.
export function useAudioCurrentTime(audioRef, ready) {
    const [time, setTime] = useState(0);
    const rafRef = useRef(0);
    useEffect(() => {
        const audio = audioRef.current;
        if (!audio)
            return;
        const tick = () => {
            setTime(audio.currentTime);
            rafRef.current = requestAnimationFrame(tick);
        };
        const start = () => {
            cancelAnimationFrame(rafRef.current);
            rafRef.current = requestAnimationFrame(tick);
        };
        const stop = () => {
            cancelAnimationFrame(rafRef.current);
            setTime(audio.currentTime);
        };
        audio.addEventListener("play", start);
        audio.addEventListener("pause", stop);
        audio.addEventListener("seeked", stop);
        audio.addEventListener("ended", stop);
        if (!audio.paused)
            start();
        return () => {
            cancelAnimationFrame(rafRef.current);
            audio.removeEventListener("play", start);
            audio.removeEventListener("pause", stop);
            audio.removeEventListener("seeked", stop);
            audio.removeEventListener("ended", stop);
        };
    }, [audioRef, ready]);
    return time;
}
